import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { PurchaseOrder, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot } from '@shared/utils';
import { generateCode, generatePartnerCode, nextDocumentBranchNumber, writeAuditLog } from '@shared/utils';

// 明細行スキーマ（納品書・請求書と整合。空白行レイアウト保持のため title は空文字も許可）
const lineItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  qty: z.number().min(0),
  unitId: z.string(),
  unitPrice: z.number().finite(),
  amount: z.number().finite(),
  taxRate: z.union([z.literal(10), z.literal(8), z.literal('exempt')]),
});

const purchaseOrderCreateSchema = z.object({
  type: z.enum(['outbound', 'inbound']),
  orderId: z.string().nullable().default(null),
  assignedUserId: z.string().nullable().default(null),
  // 取引先IDは手動入力しない。継承時は継承元の取引先ID、無ければ作成時に自動採番するため任意。
  partnerId: z.string().default(''),
  deliveryDate: z.custom<import('firebase/firestore').Timestamp | null>((v) => v === null || typeof (v as Record<string, unknown>)['seconds'] === 'number').nullable().default(null),
  lineItems: z.array(lineItemSchema).default([]),
  subtotal: z.number().min(0).default(0),
  taxAmount: z.number().min(0).default(0),
  total: z.number().min(0),
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  notes: z.string().nullable().default(null),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
});

export type PurchaseOrderCreateInput = z.infer<typeof purchaseOrderCreateSchema>;
export type PurchaseOrderUpdateInput = Partial<Omit<PurchaseOrderCreateInput, 'type'>>;

const COL = 'purchase_orders';

export async function fetchPurchaseOrders(filters?: {
  type?: 'outbound' | 'inbound';
  orderId?: ID;
}): Promise<PurchaseOrder[]> {
  let q = query(collection(db, COL), where('isDeleted', '==', false), orderBy('createdAt', 'desc'));
  if (filters?.type) q = query(q, where('type', '==', filters.type));
  if (filters?.orderId) q = query(q, where('orderId', '==', filters.orderId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PurchaseOrder);
}

export async function fetchPurchaseOrder(id: ID): Promise<PurchaseOrder | null> {
  return fromSnapshot<PurchaseOrder>(await getDoc(doc(db, COL, id)));
}

export async function createPurchaseOrder(data: PurchaseOrderCreateInput, userId: ID): Promise<PurchaseOrder> {
  const validated = parseOrThrow(purchaseOrderCreateSchema, data);
  // 取引先IDは手動入力しない。継承元の取引先IDがあればそれを使い、無ければ自動採番する。
  const partnerId = (validated.partnerId ?? '').trim() || (await generatePartnerCode());
  // 案件帳票は「受注番号-枝番」(0001-01) を code に持つ。案件に紐づかない場合のみ PREFIX-00001。
  const branch = await nextDocumentBranchNumber('purchase_orders', validated.orderId ?? null);
  const code = branch ?? (await generateCode('PO'));
  const payload = {
    ...validated, partnerId, code,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({ entityType: 'purchase_order', entityId: ref.id, action: 'create', userId, after: { ...validated, partnerId } });
  return { id: ref.id, ...payload } as unknown as PurchaseOrder;
}

export async function updatePurchaseOrder(
  id: ID,
  data: PurchaseOrderUpdateInput,
  userId: ID
): Promise<PurchaseOrder> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('注文書が見つかりません。');
  const patch = { ...data, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({ entityType: 'purchase_order', entityId: id, action: 'update', userId, before: snap.data(), after: data });
  return { id, ...snap.data(), ...patch } as unknown as PurchaseOrder;
}

export async function deletePurchaseOrder(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('注文書が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({ entityType: 'purchase_order', entityId: id, action: 'delete', userId, before: snap.data() });
}
